////////////////////////////////////////////////////////////
//  Program: PayoutBreakDown.jsx                          //
//  Application: OH HTML                                  //
//  Option: Payout Breakdown                              //
//  Developer: AK		                                      //
//  Date: 2025-05-07                                      //
//                                                        //
////////////////////////////////////////////////////////////

import { useState, useEffect, useMemo } from "react";
import queryString from "query-string";
import PayoutBreakDownService from "../../services/PayoutBreakDownService";
import "../../../assets/css/payoutBreakdown.css";

const queryParams = queryString.parse(location.search);

// Store the token in sessionStorage for API calls
if (queryParams.authToken && queryParams.platformKey) {
  sessionStorage.setItem("headerToken", queryParams.authToken);
  sessionStorage.setItem("platFormKey", queryParams.platformKey);
}

const MONTHS = {
  Jan: 0,
  Feb: 1,
  Mar: 2,
  Apr: 3,
  May: 4,
  Jun: 5,
  Jul: 6,
  Aug: 7,
  Sep: 8,
  Oct: 9,
  Nov: 10,
  Dec: 11,
};

const PayoutBreakDown = () => {
  const [payoutType] = useState(queryParams.payoutType || "resident");
  const [propertySlug] = useState(queryParams.propertySlug);
  const [isFuture] = useState(queryParams.isFuture);
  const [year, setYear] = useState(new Date().getFullYear());
  const [payoutData, setPayoutData] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(null);
  const [totalPayout, setAllPayout] = useState(null);
  const [loading, setLoading] = useState(true); // Add loading state

  useEffect(() => {
    fetchPayoutData();
  }, [payoutType, year, propertySlug, isFuture]);

  const fetchPayoutData = async () => {
    setLoading(true); // Set loading to true while fetching data
    try {
      const query =
        "payoutType=" +
        payoutType +
        "&propertySlug=" +
        propertySlug +
        (isFuture ? "&isFuture=" + isFuture : "");
      const header = {
        authToken: sessionStorage.getItem("headerToken"),
        platformKey: sessionStorage.getItem("platFormKey"),
      };

      let res = await PayoutBreakDownService.getPayoutDetails(header, query);

      if (global.successStatus.includes(res.status)) {
        const data = (await res.data.payoutDetails) ?? [];
        const currentMonth = await res.data.currentMonthPayout;
        const totalPayout = await res.data.totalPayout;
        setPayoutData(data);
        setCurrentMonth(currentMonth);
        setAllPayout(totalPayout);
      }
    } catch (error) {
      console.error("Error fetching payout data:", error);
      setPayoutData([]); // Set empty data on error
      setCurrentMonth(null); // Set empty data if API fails
      setAllPayout(null); // Set empty data if API fails
    } finally {
      setLoading(false); // Set loading to false after fetching
    }
  };
  // Helper to get current month and year in "Jun 25" format
  const getCurrentMonthYear = () => {
    const now = new Date();
    const month = now.toLocaleString("en-US", { month: "short" });
    const year = now.getFullYear().toString().slice(-2);
    return `${month} ${year}`;
  };

  // Memoize sorted payout data for performance
  const sortedPayoutData = useMemo(() => {
    return [...payoutData].sort((a, b) => {
      // Extract month and year from "Jun 24" or "Jun"
      const [aMonthStr, aYearStr] = (a.month || "").split(" ");
      const [bMonthStr, bYearStr] = (b.month || "").split(" ");
      const aMonth = MONTHS[aMonthStr] ?? -1;
      const bMonth = MONTHS[bMonthStr] ?? -1;
      const aYear = aYearStr ? parseInt(aYearStr, 10) : 0;
      const bYear = bYearStr ? parseInt(bYearStr, 10) : 0;
      // Sort by year first, then month
      if (aYear !== bYear) return aYear - bYear;
      return aMonth - bMonth;
    });
  }, [payoutData]);

  return (
    <div className="payout-breakdown container">
      <header className="mobile-header">
        <h1 className="page-title capitalize">
          {payoutType == "resident-current"
            ? "Resident Earning This Month (" + getCurrentMonthYear() + ")"
            : payoutType == "resident" && !isFuture
            ? "Resident Earning All Time"
            : payoutType == "resident" && isFuture == 1
            ? "Resident Future Earnings"
            : payoutType == "property-current"
            ? "Property Earning This Month (" + getCurrentMonthYear() + ")"
            : payoutType == "property" && !isFuture
            ? "Property Earning All Time"
            : payoutType == "property" && isFuture == 1
            ? "Property Future Earnings"
            : payoutType == "orionhaus-current"
            ? "Orion Haus Earning This Month (" + getCurrentMonthYear() + ")"
            : payoutType == "orionhaus" && !isFuture
            ? "Orion Haus Earning All Time"
            : payoutType == "orionhaus" && isFuture == 1
            ? "Orion Haus Future Earnings"
            : payoutType == "gross-current"
            ? "Gross Revenue This Month (" + getCurrentMonthYear() + ")"
            : payoutType == "gross" && !isFuture
            ? "Gross Revenue All Time"
            : payoutType == "gross" && isFuture == 1
            ? "Gross Future Earnings"
            : payoutType == "unit-rent-current"
            ? "Current Month Collected Rent (" + getCurrentMonthYear() + ")"
            : payoutType == "unit-rent"
            ? "Lifetime Collected Rent"
            : ""}{" "}
        </h1>
      </header>

      <div className="payout-table">
        {loading ? (
          <p className="loading-message">Loading data...</p>
        ) : sortedPayoutData.length > 0 ? (
          <table>
            <thead>
              <tr>
                {/* Show "Month" column only for *_current payout types */}
                {(payoutType === "resident" ||
                  payoutType === "gross" ||
                  payoutType === "property" ||
                  payoutType === "orionhaus" ||
                  payoutType === "unit-rent") && <th>Month</th>}
                {payoutData.some((payout) => payout.confirmation_code) && (
                  <th>Confirmation Code</th>
                )}
                {payoutData.some((payout) => payout.unit_name) && (
                  <th>Unit Name</th>
                )}
                <th>Amount (USD)</th>
              </tr>
            </thead>
            <tbody>
              {sortedPayoutData.map((payout, index) => (
                <tr key={index}>
                  {(payoutType === "resident" ||
                    payoutType === "gross" ||
                    payoutType === "property" ||
                    payoutType === "orionhaus" ||
                    payoutType === "unit-rent") && <td>{payout.month}</td>}
                  {payout.confirmation_code && (
                    <td>{payout.confirmation_code}</td>
                  )}
                  {payout.unit_name && <td>{payout.unit_name}</td>}
                  <td>${parseFloat(payout.payout).toLocaleString()}</td>
                  {/* Format amount */}
                </tr>
              ))}
              {/* Add a total row at the end */}
              <tr className="total-row">
                {/* Adjust colspan based on columns shown */}
                <td>
                  <strong>Total</strong>
                </td>
                <td>
                  <strong>
                    $
                    {payoutData
                      .reduce(
                        (acc, payout) => acc + parseFloat(payout.payout || 0),
                        0
                      )
                      .toLocaleString()}
                  </strong>
                </td>
              </tr>
            </tbody>
          </table>
        ) : (
          <p className="no-data-message">No data available.</p>
        )}
      </div>
    </div>
  );
};

export default PayoutBreakDown;
